import { useQuery } from "@tanstack/react-query";
import { Space } from "antd";
import { useMemo } from "react";
import { fetchDossiers } from "@/lib/api/resources/dossiers";
import { DossierFilterValues } from "@/pages/admin/dossiers/lib/types/DossierFilterValues.interface";
import { claimDossierColumns } from "@/pages/admin/dossiers/lib/constants/dossierColumns";
import { DossiersFilterModal } from "../dossiers/components/DossiersFilterModal";
import { DossiersColumnsModal } from "../dossiers/components/DossiersColumnsModal";
import { dossierFilterFields } from "../dossiers/lib/constants/dossierFilterFields";
import { useUrlFilters } from "../dossiers/lib/urlState/useUrlFilters";
import { useUrlPagination } from "../dossiers/lib/urlState/useUrlPagination";
import { useUrlColumns } from "../dossiers/lib/urlState/useUrlColumns";
import { buildDossierApiFilters } from "../dossiers/lib/helpers/buildDossierApiFilters";
import { Table } from "@/components/v2";

export const ClaimsDossiersList = () => {
  const filterKeys = dossierFilterFields.map(
    (f) => f.name,
  ) as (keyof DossierFilterValues)[];

  const { filters, setFilters } = useUrlFilters<DossierFilterValues>({
    keys: filterKeys,
    paramPrefix: "d_",
    resetPageParam: "page", // atomic page reset on filter change
  });

  const { value: page, setValue: setPage } = useUrlPagination({
    param: "page",
  });

  const setDossierFilters = (filterValues: DossierFilterValues) => {
    setFilters(filterValues);
  };

  const columns = claimDossierColumns;
  const { visibleKeys, setVisibleKeys, persistSelection, setPersist } =
    useUrlColumns({
      allKeys: columns.map((c) => c.key as string),
      param: "cols",
      storage: { key: "dossiers.columns" },
    });

  const visibleColumns = useMemo(
    () => columns.filter((col) => visibleKeys.includes(col.key as string)),
    [columns, visibleKeys],
  );

  const dossiersQuery = useQuery({
    queryKey: ["dossiers", page, filters],
    queryFn: () =>
      fetchDossiers({
        page,
        ...buildDossierApiFilters(filters),
      }),
  });

  return (
    <>
      <Space size="middle" className="mb-4">
        <DossiersFilterModal
          setDossierFilters={setDossierFilters}
          dossierFilters={filters}
        />
        <DossiersColumnsModal
          visibleKeys={visibleKeys}
          setVisibleKeys={setVisibleKeys}
          remember={persistSelection}
          setRemember={setPersist}
        />
      </Space>

      <Table
        dataSource={dossiersQuery.data?.member}
        columns={visibleColumns}
        loading={dossiersQuery.isLoading}
        pagination={{
          pageSize: 15,
          total: dossiersQuery.data?.totalItems,
          showSizeChanger: false,
          onChange: (page) => setPage(page),
        }}
      />
    </>
  );
};
